import React, { useState, useEffect } from "react";
import { Drawer, Stack, Button, Typography } from "@mui/material";
import ChartViewInput from "./ChartViewInput.jsx";
import ChartViewPort from "./ChartViewPort.jsx";
import { isEqual } from "lodash";

// import Button from "./Button.jsx";
import { ScenarioUrl_spec, empty_uuid, ftchPlus, getOptions } from "./Account_urls.jsx";
import { GoogleAnalyticsEvent } from "../Components/ga4.jsx";
import { TopOffset } from "./Navbar_bs.jsx";
import { useWindowDimensions } from "./useWindowDimensions.jsx";
import { ChartCompareInput2 } from "./ChartCompareInput.jsx";
import { errorCheckForm, errorJsonHasErrors } from "../Components/Inputs/Input_Fns.jsx";
import { Arrayify } from "../Functions/utils.jsx";


const chartsAnnual = {
  name: "Annual",
  chart_rows: [
    [{
      uuid: empty_uuid,
      name: "Gross Income",
      chart: "chart",
      arg: "?accountNames=Gross*&inout=true&sumAccountType=true&ignoreReturnType=true",
      id: "Annual_GrossIncome",
    },
    {
      uuid: empty_uuid,
      name: "Detailed Gross Income",
      chart: "chart",
      arg: "?accountNames=Gross*&inout=true",
      id: "Annual_GrossDetail",
    }],
    [{
      uuid: empty_uuid,
      name: "Taxable Income",
      chart: "chart",
      arg: "?accountNames=TaxableIncome*&inout=true",
      id: "Annual_TaxableIncome",
    },
    {
      uuid: empty_uuid,
      name: "Net After Tax",
      chart: "chart",
      arg: "?accountNames=Net*&inout=true",
      id: "Annual_Net",
    },
    {
      uuid: empty_uuid,
      name: "Surplus",
      chart: "chart",
      arg: "?accountNames=Surplus*&inout=true",
      id: "Annual_Surplus",
    }],
    [{
      uuid: empty_uuid,
      name: "Gov't Benefits",
      chart: "chart",
      arg: "?accountNames=CPP*,QPP*,OAS*,GIS*,GovernmentCredits*&inout=true",
      id: "Annual_GovernmentBenefits",
    }],
    [{
      uuid: empty_uuid,
      name: "Deductions & Payroll Taxes",
      chart: "chart",
      arg: "?accountNames=Deductions*,PayrollTaxDebit*&inout=true",
      id: "Annual_Deductions",
    }],
    [{
      uuid: empty_uuid,
      name: "Expenses by Account",
      chart: "chart",
      arg: "?accountTypes=Expenses&byAcct=true",
      id: "Annual_Expenses",
    }],
    [{
      uuid: empty_uuid,
      name: "Loan Payments & Carrying Costs",
      chart: "chart",
      arg: "?accountNames=LoanPayments*,CarryingCosts*&accountTypes=CarryingCosts&inout=true",
      id: "Annual_LoanPayments",
    }],
    [{
      uuid: empty_uuid,
      name: "Contributions",
      chart: "chart",
      arg: "?accountNames=AfterTaxContributions*&inout=true",
      id: "Annual_Contributions",
    }],
    [{
      uuid: empty_uuid,
      name: "Fees",
      chart: "chart",
      arg: "?accountNames=Fees*&cumulative=Cumulative%20Fees&inout=true",
      id: "Annual_Fees",
    }],
    [{
      uuid: empty_uuid,
      name: "Tax",
      chart: "taxchart?summary=true",
      arg: "",
      id: "Annual_TaxSummary",
    },
    {
      uuid: empty_uuid,
      name: "Tax Details",
      chart: "taxchart",
      arg: "",
      id: "Annual_TaxAmounts",
    },
    {
      uuid: empty_uuid,
      name: "Tax Rates",
      chart: "taxratechart",
      arg: "",
      id: "Annual_TaxRates",
    }],
    [{
      uuid: empty_uuid,
      name: "Cash Flow",
      chart: "sankeychart",
      arg: "?sumAccountType=true&ignoreReturnType=true",
      id: "Annual_CashFlow",
    }]],
}


const chartsInvestments = {
  name: "Investments",
  chart_rows: [
    [{
      uuid: empty_uuid,
      name: "NonRegistered by Account",
      chart: "chart",
      arg: "?accountTypes=NonRegistered,InvestLoan&cost=Cost&byAcct=true",
      id: "Investments_NonRegistered",
    },
    {
      uuid: empty_uuid,
      name: "NonRegistered In/Out",
      chart: "chart",
      arg: "?accountTypes=NonRegistered,InvestLoan&cost=Cost&inout=true",
      id: "Investments_NonRegisteredInOut",
    }],
    [{
      uuid: empty_uuid,
      name: "TFSA By Account",
      chart: "chart",
      arg: "?accountTypes=TFSA&cumulative=Contributions&byAcct=true",
      id: "Investments_Tfsa",
    },
    {
      uuid: empty_uuid,
      name: "TFSA In/Out",
      chart: "chart",
      arg: "?accountTypes=TFSA&cumulative=Contributions&inout=true",
      id: "Investments_TfsaInOut",
    }],
    [{
      uuid: empty_uuid,
      name: "Retirement by Account",
      chart: "chart",
      arg: "?accountTypes=RRSP,LIRA,DCPP&cumulative=Contributions&byAcct=true",
      id: "Investments_Retirement",
    },
    {
      uuid: empty_uuid,
      name: "Retirement In/Out",
      chart: "chart",
      arg: "?accountTypes=RRSP,LIRA,DCPP&cumulative=Contributions&inout=true",
      id: "Investments_RetirementInOut",
    }],
    [{
      uuid: empty_uuid,
      name: "Investable Assets",
      chart: "chart",
      arg: "?accountNames=InvAssets*&inout=true",
      id: "Investments_InvestableAssets",
    }]
  ],
}

const chartsOverall = {
  name: "Overall",
  chart_rows: [
    [{
      uuid: empty_uuid,
      name: "Overall By Type",
      chart: "linechart",
      arg: "?accountTypes=RRSP,TFSA,NonRegistered,LIRA,DCPP,NetWorth,Property,Liabilities,InvAssets",
      id: "Overall_Type",
    }],
    [{
      uuid: empty_uuid,
      name: "Net Worth",
      chart: "chart",
      arg: "?accountNames=NetWorth*&inout=true",
      id: "Overall_NetWorth",
    },
    {
      uuid: empty_uuid,
      name: "Estate",
      chart: "chart",
      arg: "?accountNames=Est-Residual*&inout=true&ignoreReturnType=false",
      id: "Overall_Estate",
    }]
  ]
}

const chartsProperty = {
  name: "Property",
  chart_rows: [
    [{
      uuid: empty_uuid,
      name: "Property",
      chart: "chart",
      arg: "?accountTypes=Properties&inout=true&cost=Cost",
      id: "Property_PropertiesInOut",
    }],
    [{
      uuid: empty_uuid,
      name: "Liabilities by Account",
      chart: "chart",
      arg: "?accountTypes=Liabilities&byAcct=true",
      id: "Property_Liabilities",
    },
    {
      uuid: empty_uuid,
      name: "Liabilities",
      chart: "chart",
      arg: "?accountTypes=Liabilities&inout=true",
      id: "Property_LiabilitiesInOut",
    }]
  ]
};

const chartsEducation = {
  name: "Education",
  chart_rows: [
    [{
      uuid: empty_uuid,
      name: "RESP by account",
      chart: "chart",
      arg: "?accountTypes=RESP&cumulative=Contributions\%20and\%20Grants&byAcct=true",
      id: "Education_Resp",
    },
    {
      uuid: empty_uuid,
      name: "RESP in/out",
      chart: "chart",
      arg: "?accountTypes=RESP&cumulative=Contributions\%20and\%20Grants&inout=true",
      id: "Education_RespInOut",
    }],
    [{
      uuid: empty_uuid,
      name: "Education Costs",
      chart: "chart",
      arg: "?accountNames=Education*&inout=true",
      id: "Education_Costs",
    },
    {
      uuid: empty_uuid,
      name: "Education Expenses",
      chart: "chart",
      arg: "?accountNames=EducationExpense*&inout=true",
      id: "Education_Expenses",
    }],
    [{
      uuid: empty_uuid,
      name: "Education Overall",
      chart: "linechart",
      arg: "?accountNames=Education*,EducationExpense*&accountTypes=RESP",
      id: "Education_Overall",
    }]
  ]
}
// const chartsCompare = {
//   name: "Scenario Comparisons",
//   chart_rows: [
//     [{
//       uuid: empty_uuid,
//       name: "Scenario Comparison",
//       chart: "linechart",
//       arg: "?cmp=" + uuid_1() + types + names
//     },
//     {
//       uuid: empty_uuid,
//       name: "Scenario Comparison Diff",
//       chart: "diffchart/",
//       arg: "?cmp=" + uuid_1() + types + names,
//     }],
//   ]
// }


export const chartSet = [
  chartsAnnual,
  chartsInvestments,
  chartsProperty,
  chartsEducation,
  chartsOverall,
]

export const ComparePort = ({ show, onClose, children }) => {
  return (
    <>
      <Drawer anchor="left" open={show} onClose={onClose}>
        <Stack sx={{ mx: 4, mt: TopOffset }} spacing={2}>

          {children}
        </Stack>
      </Drawer>
    </>
  )
}


const ChartViewSelector = ({ show, onClose, clientJson,
  uuids = []
}) => {
  const { winHeight, winWidth } = useWindowDimensions();

  const [cshow, setCShow] = useState(false);
  const handleCClose = () => { setSingleUuid(false); setCShow(false); }
  const handleCShow = () => { setSingleUuid(false); setCShow(true); }

  const [ishow, setIShow] = useState(false);
  const handleIClose = () => setIShow(false);
  const handleIShow = () => setIShow(true);
  const [errors, setErrors] = useState({})
  const [singleUuid, setSingleUuid] = useState(false)

  const DoChart = (c) => {
    GoogleAnalyticsEvent("chart", "view", c.name, { action: "view", type: c.name })
    setChart([c]);
    handleCShow();
  };


  const [chart, setChart] = useState([
    chartSet[0].chart_rows[0][0],
  ]);


  useEffect(() => {
    console.log("UE");
    console.log(chart);
  }, [chart]);

  useEffect(() => {
    console.log("CCI");
    if (show && uuids.length > 1) {
      getInputData();
    }
  }, [show]);

  const doChart = (c) => {
    console.log("DoChart");
    console.log(c);
    DoChart(c);
  };

  const doAllChart = () => {
    let chartlist = []
    for (let group = 0; group < chartSet.length; group++) {
      for (let row = 0; row < chartSet[group].chart_rows.length; row++) {
        for (let index = 0; index < chartSet[group].chart_rows[row].length; index++) {
          chartlist.push(chartSet[group].chart_rows[row][index])
        }
      }
    }
    GoogleAnalyticsEvent("chart", "view", "all", { action: "view", type: "all" })

    setChart(chartlist);
    handleCShow();
  };

  const onLeft = () => {
    let found = false
    let ch = null
    let pch = null

    let lg = chartSet.length - 1

    for (let group = lg; group >= 0; group--) {
      let lr = chartSet[group].chart_rows.length - 1
      for (let row = chartSet[group].chart_rows.length - 1; row >= 0; row--) {
        let lc = chartSet[group].chart_rows[row].length - 1
        for (let index = lc; index >= 0; index--) {
          if (found & !ch) {
            ch = chartSet[group].chart_rows[row][lc]
            break;
          }
          if (isEqual(chart[0], chartSet[group].chart_rows[row][index])) {
            found = true
            index -= 1
            if (index >= 0) {
              ch = chartSet[group].chart_rows[row][index]
            }
            break;
          }
        }
      }
    }
    if (ch === null) {
      console.log("reset to front")
      let lr = chartSet[lg].chart_rows.length - 1
      let lc = chartSet[lg].chart_rows[lr].length - 1

      ch = chartSet[lg].chart_rows[lr][lc]
    }
    DoChart(ch);

  };

  const onRight = () => {
    let found = false
    let ch = null
    for (let group = 0; group < chartSet.length; group++) {
      for (let row = 0; row < chartSet[group].chart_rows.length; row++) {
        for (let index = 0; index < chartSet[group].chart_rows[row].length; index++) {
          if (found & !ch) {
            ch = chartSet[group].chart_rows[row][0]
            break;
          }
          if (isEqual(chart[0], chartSet[group].chart_rows[row][index])) {
            found = true
            index += 1
            if (index < chartSet[group].chart_rows[row].length) {
              ch = chartSet[group].chart_rows[row][index]
            }
            break;
          }
        }
      }
    }
    if (ch === null) {
      console.log("reset to front")
      ch = chartSet[0].chart_rows[0][0]
    }
    DoChart(ch);
  };

  const customChart = (chart) => {
    console.log(chart);
    DoChart(chart);
  };

  const [avail, setAvail] = useState({
    AccountNames: [],
    AccountTypes: [],
  });
  const [form, setForm] = useState({
    AccountNames: [],
    AccountTypes: [
      "Est-Residual",
      "InvAssets",
      "Liabilities",
      "NetWorth",
      "Property",
      "NonRegistered",
      "RRSP",
      "TFSA",
    ],
  });
  const getInputData = () => {
    var url = ScenarioUrl_spec(clientJson, Arrayify(uuids).length > 0 ? uuids[0].uuid : null, "chartinput");
    ftchPlus(url, getOptions)
      .then((json) => {
        if (json) {
          console.log(avail);
          let a = {
            ...avail,
            AccountNames: json.Avail.AccountNames,
            AccountTypes: json.Avail.AccountTypes,
          };
          console.log(a);
          setAvail(a);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const nt_Change = (names, types) => {

    let n = typeof names === "string" ? names.split(",") : names;
    console.log(names);
    let t = typeof names === "string" ? types.split(",") : types;
    console.log(types);
    setForm({
      ...form,
      "AccountNames": n,
      "AccountTypes": t,
    });

  };

  const cmpArgs = () => {
    const remUuids = uuids.slice(1);

    const params = new URLSearchParams();
    remUuids.forEach(item => params.append('cmp', item.uuid));

    // 3. Combine with your base URL
    // console.log(params.toString())
    return params.toString()
  }

  const onChartSubmit = () => {
    errorCheckForm()
    if (!errorJsonHasErrors(errors)) {
      let types =
        form.AccountTypes.length === 0 ? "" : "&accountTypes=" + String(form.AccountTypes);
      let names =
        form.AccountNames.length === 0
          ? ""
          : "&accountNames=" + String(form.AccountNames);
      let chart1 = {
        name: "Scenario Comparison",
        chart: "linechart",
        arg: "?" + cmpArgs() + types + names,
      };
      let chart2 = {
        name: "Scenario Comparison Diff",
        chart: "diffchart/",
        arg: "?" + cmpArgs() + types + names,
      };

      GoogleAnalyticsEvent("chart", "view", "compare", { action: "compare" })
      setSingleUuid(true)
      setChart([chart1, chart2]);
      setCShow(true);
      console.log(chart1);
    }
  };

  return (
    <>
      <Stack sx={{ pb: 8 }} direction="row" spacing={8}>
        <Button variant="contained" sx={{ mb: 8, width: 260 }} onClick={doAllChart}>
          All Charts
        </Button>
        <Button variant="contained" sx={{ ml: 50, width: 260 }} onClick={handleIShow}>
          Custom chart
        </Button>

      </Stack>
      {chartSet.map((charts, i) => (
        <React.Fragment key={"empty" + i}>
          <Typography id="who" variant="h6" gutterBottom>
            {charts.name}
          </Typography>
          <Stack spacing={2}>
            {charts.chart_rows.map((c_r, r) => (
              <Stack key={"cvs:" + r} direction="row" spacing={8}>
                {c_r.map((chart, j) => (
                  <Button
                    key={"cvsr:" + j}
                    variant="contained"
                    size="small"
                    sx={{ width: 260 }}
                    onClick={() => doChart(chart)}
                  >
                    {chart.name}
                  </Button>
                ))}
              </Stack>
            ))}
          </Stack>
        </React.Fragment>
      ))}
      {uuids.length > 1 && (
        <>
          <Typography id="who" variant="h6" gutterBottom>
            Comparisons
          </Typography>
          <ChartCompareInput2
            onSubmit={onChartSubmit}
            form={form}
            avail={avail}
            nt_Change={nt_Change}
            errors={errors}
            setErrors={setErrors}
          />
        </>
      )}
      <Stack>
      </Stack>

      <Stack direction="row">
        <>
          <ChartViewPort
            clientJson={clientJson}
            show={cshow}
            onClose={handleCClose}
            chart={chart}
            onLeft={onLeft}
            onRight={onRight}
            uuids={uuids}
            singleUuid={singleUuid}
          />
        </>
      </Stack>
      <ChartViewInput
        clientJson={clientJson}
        show={ishow}
        onClose={handleIClose}
        showChart={customChart}
      />
    </>
  );
};

export default ChartViewSelector;
